import React from "react";
import PropTypes from "prop-types";
// @material-ui/core components
import { makeStyles } from "@mui/styles";
import { useTheme } from "@mui/material/styles";
import {
  TableHead,
  Tooltip,
  IconButton,
  Table,
  TableRow,
  TableBody,
  TableCell,
  TablePagination,
  TableContainer,
  Paper,
  Box,
  Pagination,
  Select,
  MenuItem,
  FormControl,
  Typography,
} from "@mui/material";
// @material-ui/icons
import { Close, Check, Edit } from "@mui/icons-material";
// core components
import styles from "assets/jss/material-dashboard/components/tasksStyle";

const useStyles = makeStyles(styles);

function Tasks({
  tableHead,
  tableData,
  tableHeaderColor,
  handleViewItem,
  handleRemoveItem,
  totalCount,
  onPageChange,
  externalLimit,
  externalPagination = false,
}) {
  const classes = useStyles();
  const theme = useTheme();

  // Internal pagination state
  const [internalLimit, setInternalLimit] = React.useState(50);
  const [internalPage, setInternalPage] = React.useState(0);
  const [startVal, setStartValState] = React.useState(0);
  const [endVal, setEndValState] = React.useState(50);

  // Use external or internal values
  const page = internalPage;
  const limit = externalLimit || internalLimit;
  const count = totalCount || tableData.length;

  const handleInternalLimitChange = (event) => {
    const newLimit = parseInt(event.target.value, 10);
    setInternalLimit(newLimit);
    setEndValState(newLimit);
    setInternalPage(0);
    setStartValState(0);
  };

  const handleInternalPageChange = (event, newPage) => {
    const direction = newPage > internalPage ? "next" : "prev";

    if (direction === "next") {
      if (endVal >= count) {
        setStartValState(0);
        setEndValState(internalLimit);
      } else {
        setStartValState(endVal);
        setEndValState(endVal + internalLimit);
      }
    } else {
      if (startVal < 1) {
        setStartValState(0);
        setEndValState(internalLimit);
      } else {
        setStartValState(Math.max(0, startVal - internalLimit));
        setEndValState(startVal);
      }
    }
    setInternalPage(newPage);
  };

  const handlePageChangeWrapper = (event, newPage) => {
    if (externalPagination) {
      if (onPageChange) {
        onPageChange(newPage);
        setInternalPage(newPage);
      }
    } else {
      handleInternalPageChange(event, newPage);
    }
  };

  const handleLimitChangeWrapper = (event) => {
    // if (externalPagination) {
    //   if (onLimitChange) {
    //     onLimitChange(parseInt(event.target.value, 10));
    //   }
    // } else {
    handleInternalLimitChange(event);
    // }
  };

  const checkPropValue = (val) =>
    val && String(val).match(/^[0-9a-fA-F]{24}$/) ? null : val;

  // Determine which data to display
  const displayData = externalPagination
    ? tableData
    : tableData.slice(startVal, endVal);

  // Calculate total pages
  const totalPages = Math.ceil(count / limit);

  return (
    <Paper>
      <TableContainer sx={{ maxHeight: 440 }}>
        <Table sx={{ width: "max-content" }} className={classes.table}>
          {tableHead !== undefined ? (
            <TableHead
              sx={{ backgroundColor: theme.palette.primary }}
              className={classes[tableHeaderColor + "TableHeader"]}
            >
              <TableRow className={classes.tableHeadRow}>
                {tableHead.map((prop, key) => {
                  return (
                    <TableCell
                      className={
                        classes.tableCell + " " + classes.tableHeadCell
                      }
                      key={key}
                    >
                      {prop}
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableHead>
          ) : null}
          <TableBody>
            {displayData.map((prop, key) => {
              return (
                <TableRow key={key} className={classes.tableBodyRow}>
                  {Object.values(prop).map((prop, key) => {
                    return (
                      <React.Fragment key={key}>
                        {prop === true ? (
                          <TableCell className={classes.tableCell}>
                            <Check
                              className={
                                classes.tableActionButtonIcon +
                                " " +
                                classes.check
                              }
                            />
                          </TableCell>
                        ) : null}
                        {prop === false ? (
                          <TableCell className={classes.tableCell}>
                            <Close
                              className={
                                classes.tableActionButtonIcon +
                                " " +
                                classes.close
                              }
                            />
                          </TableCell>
                        ) : null}
                        {prop !== false && prop !== true ? (
                          <TableCell className={classes.tableCell}>
                            {checkPropValue(prop)}
                          </TableCell>
                        ) : null}
                      </React.Fragment>
                    );
                  })}
                  <TableCell className={classes.tableActions}>
                    <Tooltip
                      id="tooltip-top"
                      title="Edit Task"
                      placement="top"
                      classes={{ tooltip: classes.tooltip }}
                      onClick={() => handleViewItem(prop)}
                    >
                      <IconButton
                        aria-label="Edit"
                        className={classes.tableActionButton}
                      >
                        <Edit
                          className={
                            classes.tableActionButtonIcon + " " + classes.edit
                          }
                        />
                      </IconButton>
                    </Tooltip>
                    <Tooltip
                      id="tooltip-top-start"
                      title="Remove"
                      placement="top"
                      classes={{ tooltip: classes.tooltip }}
                      onClick={() => handleRemoveItem(prop)}
                    >
                      <IconButton
                        aria-label="Close"
                        className={classes.tableActionButton}
                      >
                        <Close
                          className={
                            classes.tableActionButtonIcon + " " + classes.close
                          }
                        />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Custom Pagination with Page Numbers */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "16px",
          borderTop: "1px solid rgba(224, 224, 224, 1)",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Rows per page:
          </Typography>
          <FormControl size="small">
            <Select
              value={limit}
              onChange={handleLimitChangeWrapper}
              sx={{ minWidth: 80 }}
            >
              <MenuItem value={50}>50</MenuItem>
              <MenuItem value={100}>100</MenuItem>
              <MenuItem value={200}>200</MenuItem>
            </Select>
          </FormControl>
          <Typography variant="body2" color="text.secondary">
            {`${page * limit + 1}-${Math.min(
              (page + 1) * limit,
              count
            )} of ${count}`}
          </Typography>
        </Box>

        <Pagination
          count={totalPages}
          page={page + 1}
          onChange={(event, value) => handlePageChangeWrapper(event, value - 1)}
          color="primary"
          showFirstButton
          showLastButton
          siblingCount={1}
          boundaryCount={1}
        />
      </Box>
    </Paper>
  );
}

Tasks.propTypes = {
  tableHead: PropTypes.array,
  tableData: PropTypes.array.isRequired,
  tableHeaderColor: PropTypes.string,
  handleViewItem: PropTypes.func,
  handleRemoveItem: PropTypes.func,
  totalCount: PropTypes.number,
  page: PropTypes.number,
  limit: PropTypes.number,
  onPageChange: PropTypes.func,
  onLimitChange: PropTypes.func,
  externalPagination: PropTypes.bool,
};

export default Tasks;
